// Simple toast utility for notifications
// This can be replaced with react-toastify or another toast library in the future

interface ToastOptions {
  duration?: number;
  position?:
    | 'top-right'
    | 'top-left'
    | 'bottom-right'
    | 'bottom-left'
    | 'top-center'
    | 'bottom-center';
}

interface PromiseToastOptions {
  pending: string;
  success: string;
  error: string;
}

// Simple in-memory toast implementation
let toastContainer: HTMLDivElement | null = null;

function createToastContainer() {
  if (!toastContainer) {
    toastContainer = document.createElement('div');
    toastContainer.id = 'toast-container';
    toastContainer.className = 'fixed top-4 right-4 z-50 space-y-2';
    document.body.appendChild(toastContainer);
  }
  return toastContainer;
}

function showToastElement(
  message: string,
  type: 'success' | 'error' | 'info' | 'warning',
  options: ToastOptions = {}
) {
  const container = createToastContainer();
  const toast = document.createElement('div');

  const typeClasses = {
    success: 'bg-green-500 text-white',
    error: 'bg-red-500 text-white',
    info: 'bg-xtnd-blue text-white',
    warning: 'bg-yellow-500 text-black',
  };

  toast.className = `
    px-4 py-3 rounded-lg shadow-lg max-w-sm transform transition-all duration-300 ease-in-out
    ${typeClasses[type]}
    opacity-0 translate-x-full
  `.trim();

  toast.textContent = message;
  container.appendChild(toast);

  // Animate in
  requestAnimationFrame(() => {
    toast.classList.remove('opacity-0', 'translate-x-full');
    toast.classList.add('opacity-100', 'translate-x-0');
  });

  // Auto remove
  const duration = options.duration || 4000;
  setTimeout(() => {
    toast.classList.add('opacity-0', 'translate-x-full');
    setTimeout(() => {
      if (container.contains(toast)) {
        container.removeChild(toast);
      }
    }, 300);
  }, duration);
}

export const showToast = {
  success: (message: string, options?: ToastOptions) => {
    showToastElement(message, 'success', options);
  },

  error: (message: string, options?: ToastOptions) => {
    showToastElement(message, 'error', options);
  },

  info: (message: string, options?: ToastOptions) => {
    showToastElement(message, 'info', options);
  },

  warning: (message: string, options?: ToastOptions) => {
    showToastElement(message, 'warning', options);
  },

  promise: async <T>(promise: Promise<T>, options: PromiseToastOptions): Promise<T> => {
    // Show pending toast
    showToastElement(options.pending, 'info', { duration: 0 });

    try {
      const result = await promise;
      showToastElement(options.success, 'success');
      return result;
    } catch (error) {
      showToastElement(options.error, 'error');
      throw error;
    }
  },
};

// Default export for backward compatibility
export default showToast;
